import { Box, Button, Divider, Link, Paper, Stack, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import moment from 'moment';
import React, { ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { AuthContext } from '../../../contexts/AuthContext';
import { FileStructureContext } from '../../../contexts/FileStructureContext';
import { GroupSettingsContext } from '../../../contexts/GroupSettingsContext';
import { TableContext } from '../../../contexts/TableContext';
import useBackwardsCompatible from '../../../hooks/useBackwardsCompatible';
import useCompaniesTableColumns from '../../../hooks/useCompaniesTableColumns';
import { getAllUsersFunc } from '../../../lib/helper';
import { CompanyFile, Dashboard, EmailBody } from '../../../types/files';
import ArrayUtils from '../../../utils/ArrayUtils';
import FileAttachment from '../../atoms/dashboard-details-v2/FileAttachment';
import MultiSelector from '../../atoms/MultiSelector';
import EmailsModal from '../../modals/tables/dashboards/rows/EmailsModal';
import CompanyMiniDeckViewer from '../../molecules/dashboard-deck/CompanyMiniDeckViewer';

const edjsParser = require("editorjs-parser").default;
const parser = new edjsParser();

type TimelineCardProps = {
	dashboard: Dashboard|null;
};

const useStyles = makeStyles((theme) => ({
    paper: {
        borderRadius: theme.spacing(1),
    },
    header: {
        padding: theme.spacing(3),
        borderBottom: '1px solid #f1f3f5',
    },
    headerTitle: {
        fontSize: 16,
        fontWeight: 600,
        color: '#2c3e50',
        marginBottom: theme.spacing(2),
    },
    filterLabel: {
        fontSize: 13,
        color: '#6c757d',
        fontWeight: 500,
    },
    filterButton: {
        textTransform: 'capitalize',
        fontSize: 13,
    },
    filterButtonActive: {
        backgroundColor: '#1a8c8c',
        '&:hover': {
            backgroundColor: '#167676',
        },
    },
    content: {
        padding: theme.spacing(3),
    },
    dateColumn: {
        width: 120,
        textAlign: 'right',
        paddingRight: theme.spacing(3),
        borderRight: '2px solid #e9ecef',
        position: 'relative',
        '&::after': {
            content: '""',
            position: 'absolute',
            right: -7,
            top: 4,
            width: 12,
            height: 12,
            backgroundColor: '#1a8c8c',
            borderRadius: '50%',
        },
    },
    dateText: {
        fontSize: 13,
        color: '#6c757d',
        fontWeight: 500,
    },
    contentColumn: {
        flex: 1,
        paddingLeft: theme.spacing(1),
    },
    updateType: {
        fontSize: 14,
        fontWeight: 400,
        color: '#6c757d',
        marginBottom: theme.spacing(1),
		position: 'relative',
		paddingLeft: '12px',
		"&::before": {
		  content: '""',
		  width: '8px',
		  height: '8px',
		  borderRadius: '50%',
		  background: '#f59e0b',
		  position: 'absolute',
		  left: 0,
		  top: '50%',
		  transform: 'translateY(-50%)'
		}
    },
    newType: {
        fontSize: 14,
        fontWeight: 400,
        color: '#6c757d',
        marginBottom: theme.spacing(1),
		position: 'relative',
		paddingLeft: '12px',
		"&::before": {
		  content: '""',
		  width: '8px',
		  height: '8px',
		  borderRadius: '50%',
		  background: '#408bdf',
		  position: 'absolute',
		  left: 0,
		  top: '50%',
		  transform: 'translateY(-50%)'
		}
    },
    subject: {
        fontSize: 15,
        fontWeight: 600,
        color: '#2c3e50',
        marginBottom: theme.spacing(2),
    },
    dataFieldBox: {
        border: '1px solid #e9ecef',
        padding: theme.spacing(2),
        backgroundColor: '#fafbfc',
        borderRadius: theme.spacing(1),
    },
    fieldLabel: {
        fontSize: 12,
        color: '#6c757d',
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: 0.5,
        marginBottom: theme.spacing(1),
    },
    fieldText: {
        fontSize: 14,
        color: '#2c3e50',
        lineHeight: 1.6,
    },
    emailMeta: {
        fontSize: 13,
        color: '#adb5bd',
        marginTop: theme.spacing(1.5),
    },
    viewEmailLink: {
        marginTop: theme.spacing(1.5),
        display: 'inline-block',
        color: '#1a8c8c',
        textDecoration: 'none',
        fontSize: 13,
        fontWeight: 500,
        '&:hover': {
            textDecoration: 'underline',
            color: '#167676',
        },
    },
}));

const TimelineCard: React.FC<TimelineCardProps> = ({ dashboard }) => {
    const classes = useStyles();
	const { userGroup, user } = useContext(AuthContext);
	const { tagGroups, customFields } = useContext(GroupSettingsContext);
	const { fileStructure } = useContext(FileStructureContext);
    const { headers } = useContext(TableContext);
    const { defaultColumns } = useCompaniesTableColumns();
    const { dashboardOwner } = useBackwardsCompatible();

    const [comModalOpen, setComModalOpen] = useState<boolean>(false);
	const [deckViewerModalOpen, setDeckViewerModalOpen] = useState<boolean>(false);
	const [selectedFile, setSelectedFile] = useState<CompanyFile|undefined>(undefined);
    const [emailActivity, setEmailActivity] = useState<EmailBody>({} as EmailBody);
	const [email, setEmail] = useState<string>('');
    const [name, setName] = useState<{firstName: string, lastName: string}>({firstName: '', lastName: ''});
    const [filter, setFilter] = useState<string>('All');

    const owner = useMemo(() => dashboardOwner(dashboard!), [dashboard, dashboardOwner]);

	const emailActivities = useMemo(() => {
        const activityItems: EmailBody[] = dashboard?.emailBodies
            ?.filter(emailBody => ['EMAIL', 'FULL_EMAIL'].includes(emailBody?.type || '')) as EmailBody[];

        return ArrayUtils.sortByAscending(activityItems ?? [], 'createdAt');
    }, [dashboard]);

    const [createEmail, ...otherEmails] = useMemo(() =>
        emailActivities.filter(emailBody => {
            // const senderName = emailBody?.from?.replace(/<.*?>/g, '')?.trim()?.toLowerCase();
            // const senderEmail = emailBody?.from?.match(/<(.+?)>/)?.[1]?.trim()?.toLowerCase();

			return true;
            // return ((senderEmail === email) || (senderName.includes(name.firstName) && senderName.includes(name.lastName)));
        }) as EmailBody[]
	// eslint-disable-next-line
    , [email, name, emailActivities]);

    const commentaryNote = useMemo(() => {
        const commentary = dashboard?.notes?.find(note => note.title === `$['Commentary']`);
		return new DOMParser().parseFromString(parser.parse({ blocks: (JSON.parse(commentary?.data || '[]') as any[]).slice(1) }), 'text/html')?.body?.textContent?.trim() || '';
    }, [dashboard?.notes]);

	const files = useMemo(() => fileStructure
        .filter(file => !file.isDirectory && file.dashboardId === dashboard?.id)
        .sort((dA, dB) => moment(dB.createdAt).diff(moment(dA.createdAt)))
    , [dashboard, fileStructure]);

	const lookUpTagsSelection = useCallback((tag: string) =>
		dashboard?.tagGroups?.find(({ title }) => title.toLowerCase() === tag.toLowerCase())?.tags || []
	, [dashboard]);

    const lookUpCustomFields = useCallback((fieldName: string) => {
        const value = dashboard?.customFields?.find(({ name }) => name === fieldName)?.value;
        return Array.isArray(value) ? value : value ? [value] : [];
    }, [dashboard]);

    const filterOptions = useMemo(() => {
        const columns = headers ?? defaultColumns.map(col => col.id) as string[];
        const tagGroupsOptions = tagGroups.filter(({ title }) => title !== 'Sectors' && !!lookUpTagsSelection(title)?.length).map(({ title }) => title);
        const customFieldOptions = customFields.filter(({ name }) => !!lookUpCustomFields(name)).map(({ name }) => name);
        const attachmentOptions = [!!files?.length ? 'Files' : null, !!dashboard?.externalLinks?.length ? 'Deck/Teaser' : null].filter(option => option !== null);

        return ['All', ...attachmentOptions, ...(columns || [])?.filter(column => (!!commentaryNote && ['Commentary'].includes(column))
            || tagGroupsOptions.includes(column) || customFieldOptions.includes(column)
        )] as string[];
    // eslint-disable-next-line
    }, [dashboard, files, tagGroups, customFields, commentaryNote, headers, defaultColumns]);

    const RenderFields: {[key: string]: ReactNode} = useMemo(() => ({
        'Commentary': !!commentaryNote && (filter === 'All' || filter === 'Commentary') ? (
            <Box className={classes.dataFieldBox}>
                <Typography className={classes.fieldLabel}>{'Commentary'}</Typography>
                <Typography className={classes.fieldText}>
                    {commentaryNote}
                </Typography>
            </Box>
        ) : null,
        'Files': !!files?.length && (filter === 'All' || filter === 'Files') ? (
            <Box className={classes.dataFieldBox}>
                <Typography className={classes.fieldLabel}>
                    {`Attachments (${files?.length})`}
                </Typography>
                <Stack direction="row" flexWrap="wrap" gap={1}>
                    {files?.map((file, i) => (
                        <Box onClick={() => {
                            setSelectedFile(file);
                            setDeckViewerModalOpen(true);
                        }} key={'dashboard-details-page-909-' + i}>
                            <FileAttachment resName={file.name}
                                file={file} isPdf={file.name.endsWith('.pdf')} />
                        </Box>
                    ))}
                </Stack>
            </Box>
        ) : null,
        'Deck/Teaser': !!dashboard?.externalLinks?.length && (filter === 'All' || filter === 'Deck/Teaser') ? (
            <Box className={classes.dataFieldBox}>
                <Typography className={classes.fieldLabel}>
                    {`Deck/Teaser (${dashboard?.externalLinks?.length || 0})`}
                </Typography>
                <Stack direction="row" flexWrap="wrap" gap={1}>
                    {dashboard?.externalLinks?.map((link, i, links) => (
                        <Box key={'dashboard-details-page-924-' + i}>
                            <FileAttachment resName={link}
                                file={files?.length === 1 && links?.length === 1 ? files[0] : undefined} />
                        </Box>
                    ))}
                </Stack>
            </Box>
        ) : null,
        ...Object.fromEntries(tagGroups.filter(({ title }) => title !== 'Sectors')
            .map(({ title, tags }, i) => !!lookUpTagsSelection(title)?.length
                && (filter === 'All' || filter === title) ? [title, (
                    <Box className={classes.dataFieldBox} key={'dashboard-details-page-860-' + i}>
                        <Typography className={classes.fieldLabel}>{title}</Typography>
                        <Box sx={{ transform: 'scale(1.1) transformX(-10px)' }}>
                            <MultiSelector
                                mainOptions={tags}
                                selections={lookUpTagsSelection(title)}
                                fullSpan readOnly/>
                        </Box>
                    </Box>
        )] : [title, null])),
        ...Object.fromEntries(customFields.map(({ name }, i) => !!lookUpCustomFields(name)?.length
            && (filter === 'All' || filter === name) ? [name, (
                <Box className={classes.dataFieldBox} key={'dashboard-details-page-871-' + i}>
                    <Typography className={classes.fieldLabel}>{name}</Typography>
                    <Typography className={classes.fieldText}>
                        {lookUpCustomFields(name)}
                    </Typography>
                </Box>
        )]: [name, null])),
    // eslint-disable-next-line
    }), [dashboard, commentaryNote, files, tagGroups, customFields, filter]);

	useEffect(() => {
		getAllUsersFunc(userGroup).then((users) => {
			const lookUpUser = users.find(({ username }) => username === user.username);

			setEmail(user?.attributes?.email?.toLowerCase() || lookUpUser?.email?.toLowerCase());
			setName({ firstName: lookUpUser?.firstName?.toLowerCase() || '', lastName: lookUpUser?.lastName?.toLowerCase() || '' });
		});
	}, [userGroup, user]);

    return (<>
        <Paper elevation={0} className={classes.paper}>
            <Box className={classes.header}>
                <Typography className={classes.headerTitle}>Activity Timeline</Typography>
                <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
                    <Typography className={classes.filterLabel}>Show:</Typography>
                    {filterOptions.map((f) => (
                        <Button key={f} size="small"
                            variant={filter === f ? 'contained' : 'outlined'}
                            className={`${classes.filterButton}
								${filter === f ? classes.filterButtonActive : ''}
							`} onClick={() => setFilter(f)}>
                            {f === 'all' ? 'All Fields' : f}
                        </Button>
                    ))}
                </Stack>
            </Box>
            <Box className={classes.content}>
                <Stack spacing={3} divider={<Divider />}>
                    {otherEmails?.map((email, index) => (
                        <Stack key={index} direction="row" spacing={3}>
                            <Box className={classes.dateColumn}>
                                <Typography className={classes.dateText}>{moment(email.createdAt).format("MMM D, YYYY")}</Typography>
                            </Box>
                            <Box className={classes.contentColumn}>
                                {email.contentClassification === 'COMPANY_UPDATE' ? (
                                    <Typography className={classes.updateType}>
                                        {'Company Update'}
                                    </Typography>
                                ) : (
                                    <Typography className={classes.newType}>
                                        {'New deal'}
                                    </Typography>
                                )}
                                <Typography className={classes.subject}>{email.subject}</Typography>
                                <Typography className={classes.emailMeta}>
                                    {`From: ${email?.from?.replace(/<.*?>/g, '')}`}
                                </Typography>
                                <Link href="#" className={classes.viewEmailLink}
									onClick={() => {setEmailActivity(email); setComModalOpen(true);}}>
                                    {'View full email →'}
                                </Link>
                            </Box>
                        </Stack>
                    ))}
					{createEmail ? (
						<Stack direction="row" spacing={3}>
							<Box className={classes.dateColumn}>
								<Typography className={classes.dateText}>
									{moment(dashboard?.createdAt).format("MMM D, YYYY")}
								</Typography>
							</Box>
							<Box className={classes.contentColumn}>
                                {createEmail.contentClassification === 'COMPANY_UPDATE' ? (
                                    <Typography className={classes.updateType}>
                                        {'Company Update'}
                                    </Typography>
                                ) : (
                                    <Typography className={classes.newType}>
                                        {'New deal'}
                                    </Typography>
                                )}
								<Typography className={classes.subject}>{createEmail.subject}</Typography>
								<Stack spacing={2}>
                                    {filterOptions?.map(field => RenderFields[field])}
								</Stack>
								<Typography className={classes.emailMeta}>
									{`From: ${createEmail?.from?.replace(/<.*?>/g, '')}`}
								</Typography>
								<Link href="#" className={classes.viewEmailLink}
									onClick={() => {setEmailActivity(createEmail); setComModalOpen(true);}}>
									{`View full email →`}
								</Link>
							</Box>
						</Stack>
					) : !!files?.length && (
						<Stack direction="row" spacing={3}>
							<Box className={classes.dateColumn}>
								<Typography className={classes.dateText}>
									{moment(dashboard?.createdAt).format("MMM D, YYYY")}
								</Typography>
							</Box>
							<Box className={classes.contentColumn}>
                                <Typography className={classes.newType}>
                                    {'New deal'}
                                </Typography>
								<Typography className={classes.subject}>{'File uploaded'}</Typography>
								<Stack spacing={2}>
                                    {filterOptions?.map(field => RenderFields[field])}
								</Stack>
								<Typography className={classes.emailMeta}>
									{`Added by: ${owner?.name}`}
								</Typography>
							</Box>
						</Stack>
					)}
                    {!otherEmails.length && !createEmail && !files?.length && (
                        <Stack alignItems="center" justifyContent="center" height="50vh" pb={4}>
                            <Typography fontFamily="Lato" fontWeight="600" color="#2c3e50">
                                {'No email or files yet.'}
                            </Typography>
                        </Stack>
                    )}
                </Stack>
            </Box>
        </Paper>
        {deckViewerModalOpen && (
            <CompanyMiniDeckViewer
                open={deckViewerModalOpen}
                defaultDeck={selectedFile}
                externalDeck={dashboard?.externalLinks?.at(-1)}
                onClose={() => setDeckViewerModalOpen(false) } />
        )}
        {comModalOpen && (
            <EmailsModal open={comModalOpen}
                emailActivities={[emailActivity]}
                dashboard={dashboard!}
                onClose={() => setComModalOpen(false)} />
        )}
    </>);
};

export default TimelineCard;